import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Phone, Wifi, WifiOff, Copy, Check, Radio } from 'lucide-react';
import { toast } from 'sonner';

/* Live-test modal: shows the inbound number to call + a countdown +
   a list of "node visited" events streamed from the backend WS as soon
   as the inbound call hits Twilio.
   • status: "idle" → "connecting" → "live" → "ended"
   • The parent canvas is what actually pulses nodes — we just stream the
     events down via the `onStep`/`onFinalize` callbacks. */
export default function LiveTestModal({ session, wsStatus, lastEvent, executions, onClose, onCopy, onStop }) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!session?.expires_at) return;
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(session.expires_at).getTime() - Date.now()) / 1000));
      setSecondsLeft(left);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [session]);

  const copyNum = () => {
    if (!session?.phone_number) return;
    navigator.clipboard.writeText(session.phone_number);
    setCopied(true);
    onCopy?.();
    toast.success('Number copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const mins = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, '0');
  const wsLive = wsStatus === 'live';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="live-test-modal"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        className="w-[460px] max-w-full bg-[#0c0d12] border border-teal-400/30 rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-teal-400/15 border border-teal-400/30 flex items-center justify-center">
            <Radio size={16} className="text-teal-300 animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white">Live Test Mode</div>
            <div className="text-[10px] text-gray-400">Call this number to see the flow execute</div>
          </div>
          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${
            wsLive ? 'bg-emerald-400/15 text-emerald-300' : 'bg-amber-400/15 text-amber-300'
          }`}>
            {wsLive ? <Wifi size={10} /> : <WifiOff size={10} />} {wsLive ? 'Connected' : wsStatus || 'Connecting'}
          </span>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="live-test-close"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-xl bg-gradient-to-br from-teal-400/10 to-emerald-400/10 border border-teal-400/30 p-4">
            <div className="text-[10px] uppercase tracking-wider text-teal-300 font-bold mb-2">Dial this number</div>
            <div className="flex items-center gap-3">
              <Phone size={20} className="text-teal-300 flex-shrink-0" />
              <div className="flex-1 text-2xl font-mono font-bold text-white tracking-wide" data-testid="live-test-phone">
                {session?.phone_number || '—'}
              </div>
              <button onClick={copyNum} className="p-2 rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:text-white hover:bg-white/10" data-testid="live-test-copy">
                {copied ? <Check size={14} className="text-emerald-300" /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">Session expires in</span>
            <span className={`font-mono font-bold ${secondsLeft < 30 ? 'text-rose-300' : 'text-white'}`} data-testid="live-test-countdown">
              {mins}:{secs}
            </span>
          </div>

          <div className="rounded-xl border border-white/10 max-h-[180px] overflow-y-auto" data-testid="live-test-event-log">
            {executions.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-gray-500">
                Waiting for inbound call…
              </div>
            ) : (
              executions.slice().reverse().map((e, i) => (
                <div key={i} className="px-3 py-2 border-b border-white/5 last:border-0 text-[11px]">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      e.status === 'matched' || e.status === 'ok' ? 'bg-emerald-400' :
                      e.status === 'fallback' ? 'bg-amber-400' :
                      e.status === 'skipped' ? 'bg-gray-500' : 'bg-rose-400'
                    }`} />
                    <span className="font-bold text-white uppercase tracking-wider text-[10px]">{e.type}</span>
                    <span className="text-gray-500 text-[10px] ml-auto">{e.status}</span>
                  </div>
                  {e.reason && <div className="text-gray-400 mt-0.5 pl-3.5 truncate" title={e.reason}>{e.reason}</div>}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onStop} className="px-4 py-2 rounded-lg bg-rose-500/15 border border-rose-400/30 text-rose-300 text-xs hover:bg-rose-500/25" data-testid="live-test-stop">
            End Live Test
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
